import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { RESTAURANT } from "../data/menu";
import { Wordmark } from "./Logo";

const links = [
  { to: "/", label: "Home" },
  { to: "/menu", label: "Menu" },
  { to: "/order", label: "Order Online" },
  { to: "/book", label: "Book a Table" },
  { to: "/about", label: "About Us" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-black/90 backdrop-blur-xl border-b border-[#D4A017]/20"
          : "bg-black/40 backdrop-blur-sm"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-[170px] flex items-center justify-between gap-4">
        <Link to="/" data-testid="navbar-logo" className="shrink-0">
          <Wordmark size="3xl" />
        </Link>

        <nav className="hidden lg:flex items-center justify-center gap-7 whitespace-nowrap flex-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.replace(/\s+/g, "-").toLowerCase()}`}
              end={l.to === "/"}
              className={({ isActive }) =>
                `relative text-[12px] uppercase tracking-[0.18em] font-medium transition-colors py-1 ${
                  isActive ? "text-[#D4A017]" : "text-white/85 hover:text-[#D4A017]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  {isActive && (
                    <span className="absolute -bottom-1 left-0 right-0 h-[2px] bg-[#D4A017]" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 shrink-0">
          <a
            href={`tel:${RESTAURANT.phoneRaw}`}
            className="hidden md:inline-flex items-center gap-2 bg-[#D4A017] hover:bg-[#F0BE3F] text-black font-semibold pl-2.5 pr-4 py-1.5 rounded-full text-xs transition-colors shadow-md shadow-[#D4A017]/20"
            data-testid="navbar-call"
          >
            <span className="w-6 h-6 rounded-full bg-black/15 flex items-center justify-center">
              <Phone size={11} />
            </span>
            {RESTAURANT.phone}
          </a>

          <button
            onClick={() => setOpen(!open)}
            className="lg:hidden w-9 h-9 flex items-center justify-center rounded-full bg-white/5 border border-white/10 text-white"
            data-testid="navbar-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={17} /> : <Menu size={17} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-[#D4A017]/20" data-testid="mobile-menu">
          <nav className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`mobile-nav-link-${l.label.replace(/\s+/g, "-").toLowerCase()}`}
                className={({ isActive }) =>
                  `text-base uppercase tracking-[0.2em] ${
                    isActive ? "text-[#D4A017]" : "text-white/90"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href={`tel:${RESTAURANT.phoneRaw}`}
              className="mt-2 inline-flex items-center gap-2 bg-[#D4A017] text-black font-semibold px-4 py-2.5 rounded-full text-sm self-start"
              data-testid="mobile-call"
            >
              <Phone size={14} /> {RESTAURANT.phone}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};
